/**
 * Planner settings for someone who has no SMEST account.
 *
 * The planner normally loads these from `GET /portfolio_planner/bootstrap/
 * {user_id}/`, which needs a real user. A prospect has none, and borrowing
 * another customer's id to get them is not acceptable — so for prospects no
 * user id is used for anything, and the settings come from this copy instead.
 *
 * WHY A COPY IS SAFE HERE, when the type docs warn against hard-coding config:
 * that warning is about the form's live badges disagreeing with the generated
 * plan. For a prospect every value that shapes the plan is visible in the form
 * and editable by the admin, and is sent to the server explicitly — so the plan
 * and its PDF always match exactly what the admin entered. The PDF's figures
 * come from the server's `generate` response, not from here; the PDF reads
 * `config` only as a fallback for three rows on its assumptions page. The one
 * thing that can go stale is a *suggested default* — see `describePlannerDrift`,
 * which flags it.
 *
 * SOURCES — every value agrees across all three:
 *   1. `MF Portfolio Calculator/Portfolio Calculator API.md` §1, bootstrap sample
 *   2. the enum and default comments in ./types.ts
 *   3. `assumptions` in a live generate response, 25-08-2026
 *
 * If the backend changes a default, change it here too.
 */

import type { PlannerBootstrap, PlannerConfig, PlannerOptions } from "./types";

export const PROSPECT_PLANNER_CONFIG: PlannerConfig = {
  segBoundaries: { shortMaxYears: 3, midMaxYears: 6 },
  defaultReturn: { Debt: 7, Hybrid: 11, Equity: 12 },
  inflationDefault: 8,
  liabilitySipFactor: 0.1,
  swpDefault: { returnPct: 9, inflationPct: 6 },
  locale: "en-IN",
  currency: "INR",
};

export const PROSPECT_PLANNER_OPTIONS: PlannerOptions = {
  investorType: [
    "Salaried",
    "Self-employed / Business",
    "Professional",
    "Retired",
    "HNI",
  ],
  riskProfile: ["Aggressive", "Conservative"],
  goalType: [
    "Retirement",
    "Child Education",
    "Child Marriage",
    "Buy a House",
    "Buy a Car",
    "Wealth Creation",
    "Travel / Vacation",
    "Emergency Fund",
    "Other",
  ],
  liabilityType: [
    "Home Loan",
    "Car Loan",
    "Personal Loan",
    "Education Loan",
    "Credit Card",
    "Business Loan",
    "Other",
  ],
  taxBracket: [0, 5, 10, 20, 30],
  withdrawalFrequency: ["Monthly", "Quarterly", "Half-Yearly", "Annual"],
  sipFrequency: ["Monthly", "Quarterly", "Half-Yearly", "Annual"],
};

/** What the planner opens with for a prospect. Nothing is prefilled. */
export const PROSPECT_PLANNER_BOOTSTRAP: PlannerBootstrap = {
  config: PROSPECT_PLANNER_CONFIG,
  options: PROSPECT_PLANNER_OPTIONS,
  prefill: {
    name: null,
    ageYears: null,
    ageMonths: null,
    investorType: null,
    taxBracket: null,
    taxBracketIsSuggestion: false,
    firstTimer: null,
    // No questionnaire scoring exists yet, so this is always chosen by hand.
    riskProfile: null,
  },
};

/**
 * Where the live settings differ from this copy, or `[]` when they match.
 *
 * Run against a bootstrap the admin panel already fetched for a real investor
 * — never a lookup made for this purpose — so it costs no extra call and uses
 * no one's id beyond what the normal planner flow already does. It turns a
 * silently stale default into a visible warning.
 */
export const describePlannerDrift = (live: PlannerBootstrap): string[] => {
  const drift: string[] = [];
  const compare = (label: string, actual: unknown, copy: unknown) => {
    if (JSON.stringify(actual) !== JSON.stringify(copy)) {
      drift.push(`${label}: live ${JSON.stringify(actual)}, copy ${JSON.stringify(copy)}`);
    }
  };
  const config = live.config || ({} as PlannerConfig);
  compare("segBoundaries", config.segBoundaries, PROSPECT_PLANNER_CONFIG.segBoundaries);
  compare("defaultReturn", config.defaultReturn, PROSPECT_PLANNER_CONFIG.defaultReturn);
  compare("inflationDefault", config.inflationDefault, PROSPECT_PLANNER_CONFIG.inflationDefault);
  compare(
    "liabilitySipFactor",
    config.liabilitySipFactor,
    PROSPECT_PLANNER_CONFIG.liabilitySipFactor
  );
  compare("swpDefault", config.swpDefault, PROSPECT_PLANNER_CONFIG.swpDefault);

  const options = live.options || ({} as PlannerOptions);
  (Object.keys(PROSPECT_PLANNER_OPTIONS) as Array<keyof PlannerOptions>).forEach((key) => {
    compare(`options.${key}`, options[key], PROSPECT_PLANNER_OPTIONS[key]);
  });
  return drift;
};
